import { useState, useEffect, useRef } from 'react';
import InputField from '../../../components/Forms/Input/InputField';
import SimpleSearchSelect from '../../../components/Forms/Input/SimpleSearchSelect';
import SvgIcon from '../../SvgIcon/SvgIcon';
import { ivaTypeOptions } from '../productData';
import SelectField from '../../Forms/Input/SelectField';
import { RootState } from '../../../store/store';
import { useSelector } from 'react-redux';
import { useExcludedSubject } from '../../../hooks/purchases/useExcludedSubject';
import { useEnterToClick } from '../../../hooks/useEnter';
import { ITEM_TYPE_CODES } from '../../../constants/itemTypeCodes';
import { useProduct } from '../../../hooks/useProduct';

const AddExcludedProductForm = ({
  onAdd,
}: {
  onAdd: (product: any) => void;
}) => {
  const saveBtnRef = useRef<HTMLButtonElement>(null);
  useEnterToClick(saveBtnRef, true);

  const [form, setForm] = useState({
    detailId: null as number | null,
    productId: '',
    productName: '',
    ivaType: 'EXENTO',
    unitName: '',
    calculatedTotal: '',
    calculatedQty: '',
    unitCost: '',
    quantity: '',
    ivaTotal: '0.00000000',
    ivaUnit: '0.00000000',
    totalCost: '',
    withIva: '',
  });

  const {
    form: { id: purchaseId },
  } = useSelector((state: RootState) => state.basePurchase);

  const productToEdit = useSelector(
    (state: RootState) => state.excludedSubject.productToEdit
  );

  const { clearProductToEdit, handleCancelProduct, handlerUpdateProduct } =
    useExcludedSubject();

  const { productsDropdown, handlerGetProductsForDropdownByItemType } =
    useProduct();

  const [isManualUnitCost, setIsManualUnitCost] = useState(false);
  const [isManualQuantity, setIsManualQuantity] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // 👉 Si el usuario edita unitCost, lo marcamos como manual
    if (name === 'unitCost') setIsManualUnitCost(true);

    // 👉 Si edita cantidad manual, borra la cantidad calculada
    if (name === 'quantity') {
      setIsManualQuantity(true);
      setForm((prev) => ({
        ...prev,
        quantity: value,
        calculatedQty: '',
      }));
      return;
    }

    // 👉 Si edita cantidad calculadora, borra la cantidad manual
    if (name === 'calculatedQty') {
      setIsManualQuantity(false); // el usuario volvió al modo automático
      setForm((prev) => ({
        ...prev,
        calculatedQty: value,
        quantity: '',
      }));
      return;
    }

    // 👉 Para los demás campos, se actualiza normalmente
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const { calculatedTotal, calculatedQty, quantity, unitCost } = form;

    // 1) Si hay Total (calculadora) + alguna Cantidad (manual o calculadora)
    if (calculatedTotal && (calculatedQty || quantity) && !isManualUnitCost) {
      const total = parseFloat(calculatedTotal || '0');
      const qty = parseFloat(quantity || calculatedQty || '0');
      if (isFinite(total) && isFinite(qty) && total > 0 && qty > 0) {
        const u = total / qty;
        setForm((prev) => ({
          ...prev,
          unitCost: u.toFixed(8),
          totalCost: total.toFixed(2),
          withIva: '0.00000000',
          ivaUnit: '0.00000000',
          ivaTotal: '0.00000000',
        }));
      }
    }

    // 2) Unitario + Cantidad -> Total (independiente del punto 1)
    const qty2 = parseFloat(
      (isManualQuantity ? form.quantity : form.calculatedQty) || '0'
    );
    const u2 = parseFloat(unitCost || '0');

    if (isFinite(u2) && isFinite(qty2) && u2 > 0 && qty2 > 0) {
      const total2 = u2 * qty2;
      setForm((prev) => ({
        ...prev,
        totalCost: total2.toFixed(2),
        withIva: '0.00000000',
        ivaUnit: '0.00000000',
        ivaTotal: '0.00000000',
      }));
    }
  }, [
    form.calculatedTotal,
    form.calculatedQty,
    form.unitCost,
    form.quantity,
    isManualUnitCost,
    isManualQuantity,
  ]);


  useEffect(() => {
    if (productToEdit) {
      setForm({
        detailId: productToEdit.detailId,
        productId: String(productToEdit.productId),
        productName: productToEdit.productName || '',
        ivaType: 'EXENTO',
        unitName: '',
        calculatedTotal: '',
        calculatedQty: '',
        unitCost: String(productToEdit.unitCost),
        quantity: String(productToEdit.quantity),
        ivaTotal: '0.00000000',
        ivaUnit: '0.00000000',
        totalCost: (productToEdit.unitCost * productToEdit.quantity).toFixed(2),
        withIva: '',
      });
    }
  }, [productToEdit]);

  const handleAdd = () => {
    const errs: typeof errors = {};
    if (!form.productId) errs.productId = 'Producto requerido';
    if (!form.unitCost) errs.unitCost = 'Costo unitario requerido';
    if (!form.quantity && !form.calculatedQty)
      errs.quantity = 'Cantidad requerida';

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    if (form.detailId) {
      // 🛠 EDITAR
      const updatePayload = {
        detailId: form.detailId,
        productId: Number(form.productId),
        quantity: parseFloat(form.quantity || form.calculatedQty),
        unitCost: parseFloat(form.unitCost),
        taxType: 'EXEMPT',
      };
      handlerUpdateProduct(updatePayload);
    } else {
      // 🆕 CREAR
      const createPayload = {
        purchaseId,
        productId: Number(form.productId),
        productName: form.productName,
        quantity: parseFloat(form.quantity || form.calculatedQty),
        unitCost: parseFloat(form.unitCost),
        taxType: 'EXEMPT',
      };
      onAdd(createPayload);
    }
  };

  useEffect(() => {
    return () => {
      clearProductToEdit();
    };
  }, []);

  useEffect(() => {
    handlerGetProductsForDropdownByItemType(ITEM_TYPE_CODES.GOODS);
  }, []);

  return (
    <div className="space-y-6">
      {/* Producto + Tipo en la misma fila */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <SimpleSearchSelect
          label="Producto"
          name="productId"
          iconKey="category"
          options={productsDropdown}
          selectedValue={form.productId ? Number(form.productId) : null}
          onSelect={(option: any) =>
            setForm((prev) => ({
              ...prev,
              productId: option.id,
              productName: option.name,
            }))
          }
          error={errors.productId}
          required
        />

        <SelectField
          label="Tipo"
          name="ivaType"
          iconKey="list"
          value={form.ivaType}
          onChange={() => {}} // Solo lectura
          options={ivaTypeOptions}
          disabled
          required
        />
      </div>

      {/* Costo Unitario, Cantidad y Costo Total */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <InputField
          label="Costo Unitario"
          name="unitCost"
          iconKey="dollar"
          type="number"
          value={form.unitCost}
          onChange={handleChange}
          error={errors.unitCost}
          required
        />
        <InputField
          label="Cantidad"
          name="quantity"
          iconKey="product"
          type="number"
          value={form.quantity}
          onChange={handleChange}
          error={errors.quantity}
          required
        />
        <InputField
          label="Costo Total"
          name="totalCost"
          iconKey="dollar"
          type="number"
          value={form.totalCost}
          onChange={() => {}}
          disabled
        />
      </div>

      {/* Calculadora */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <InputField
          label="Costo Total (Calculadora)"
          name="calculatedTotal"
          iconKey="dollar"
          type="number"
          value={form.calculatedTotal}
          onChange={handleChange}
        />
        <InputField
          label="Cantidad (Calculadora)"
          name="calculatedQty"
          iconKey="product"
          type="number"
          value={form.calculatedQty}
          onChange={handleChange}
        />
        <div />
      </div>

      {/* Botones */}
      <div className="flex justify-end gap-3 pt-4">
        <button
          type="button"
          ref={saveBtnRef}
          onClick={handleAdd}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-opacity-90"
        >
          <SvgIcon name="save" className="h-5 w-5" />
          Guardar
        </button>
        <button
          type="button"
          onClick={handleCancelProduct}
          className="inline-flex items-center gap-2 rounded-md bg-[#DC3545] px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-opacity-90"
        >
          <SvgIcon name="cancel" className="h-5 w-5" />
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default AddExcludedProductForm;
